import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField
} from '@mui/material';
import { Plus, SquarePen } from 'lucide-react';
import { getContactDetails, createContactDetails, updateContactDetails } from '../../Services/ContentHub/ContactusApi';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const ContactUs = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [contactDetails, setContactDetails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentContact, setCurrentContact] = useState(null);
    const [formData, setFormData] = useState({
        mobile: '',
        email: '',
        whatsapp: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [modalError, setModalError] = useState(null);

    // Fetch contact details with useCallback to prevent multiple API calls
    const fetchContactDetails = useCallback(async (signal) => {
        try {
            setLoading(true);
            const response = await getContactDetails({ signal });
            if (response && response.success) {
                setContactDetails(response.data);
            } else {
                setError("Failed to load contact details.");
            }
        } catch (err) {
            if (!signal?.aborted && err.name !== 'CanceledError') {
                setError("Failed to fetch contact details. Please try again later.");
            }
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        fetchContactDetails(controller.signal);
        return () => controller.abort();
    }, [fetchContactDetails]);

    const handleOpenModal = (contact = null) => {
        setFieldErrors({});
        setModalError(null);
        if (contact) {
            setIsEdit(true);
            setCurrentContact(contact);
            setFormData({
                mobile: contact.mobile || '',
                email: contact.email || '',
                whatsapp: contact.whatsapp || ''
            });
        } else {
            setIsEdit(false);
            setCurrentContact(null);
            setFormData({
                mobile: '',
                email: '',
                whatsapp: ''
            });
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setFieldErrors({});
        setModalError(null);
        setOpenModal(false);
        setIsEdit(false);
        setCurrentContact(null);
        setFormData({
            mobile: '',
            email: '',
            whatsapp: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleSubmit = async () => {
        const newFieldErrors = {};
        if (!formData.mobile) newFieldErrors.mobile = "Mobile number is required";
        if (!formData.email) newFieldErrors.email = "Email is required";
        if (!formData.whatsapp) newFieldErrors.whatsapp = "WhatsApp number is required";

        if (Object.keys(newFieldErrors).length > 0) {
            setFieldErrors(newFieldErrors);
            setModalError("Please fill in all required fields");
            return;
        }

        setSubmitting(true);
        setModalError(null);
        setFieldErrors({});

        try {
            if (isEdit) {
                await updateContactDetails(currentContact.id, formData);
                showSnackbar("Contact details updated successfully!", "success");
            } else {
                await createContactDetails(formData);
                showSnackbar("Contact details created successfully!", "success");
            }

            handleCloseModal();
            // Refresh the list
            const controller = new AbortController();
            fetchContactDetails(controller.signal);
        } catch (err) {
            console.error("Error saving contact details:", err);
            if (err.response && err.response.data && err.response.data.details) {
                const errors = {};
                err.response.data.details.forEach(detail => {
                    errors[detail.field] = detail.message;
                });
                setFieldErrors(errors);
                setModalError("Invalid input details");
            } else {
                setModalError(err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} contact details`);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Contact Details
                    </Typography>
                    {contactDetails.length === 0 && !loading && (
                        <PermissionWrapper permission="create" mode="disable">
                            <Button
                                disableElevation
                                disableRipple
                                disableFocusRipple
                                disableTouchRipple
                                variant="contained"
                                startIcon={<Plus size={18} />}
                                onClick={() => handleOpenModal()}
                                sx={{
                                    bgcolor: '#FFBD65',
                                    px: 2.5,
                                    color: '#fff',
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    height: '40px',
                                    fontSize: '0.85rem'
                                }}
                            >
                                Add Contact Details
                            </Button>
                        </PermissionWrapper>
                    )}
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 950 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Mobile</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>WhatsApp</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px', textAlign: 'right' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : contactDetails.length > 0 ? (
                                    contactDetails.map((contact, idx) => (
                                        <TableRow key={contact.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 },
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#333', fontWeight: 500 }}>{contact.mobile}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>{contact.email}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>{contact.whatsapp}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {contact.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {contact.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(contact.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(contact.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small"
                                                                sx={{ color: '#FFBD65' }}
                                                                onClick={() => handleOpenModal(contact)}
                                                            >
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No contact details found. Click "Add Contact Details" to create one.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Add/Edit Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        {isEdit ? 'Edit Contact Details' : 'Add Contact Details'}
                    </DialogTitle>
                    <DialogContent>
                        {modalError && (
                            <Alert severity="error" sx={{ mt: 1, mb: 1, fontSize: '0.8rem' }} onClose={() => setModalError(null)}>
                                {modalError}
                            </Alert>
                        )}
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                fullWidth
                                name="mobile"
                                label="Mobile Number"
                                placeholder="Enter mobile number"
                                value={formData.mobile}
                                onChange={handleInputChange}
                                size='small'
                                error={Boolean(fieldErrors.mobile)}
                                helperText={fieldErrors.mobile}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <TextField
                                fullWidth
                                name="email"
                                type="email"
                                size='small'
                                label="Email"
                                placeholder="Enter email address"
                                value={formData.email}
                                onChange={handleInputChange}
                                error={Boolean(fieldErrors.email)}
                                helperText={fieldErrors.email}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <TextField
                                fullWidth
                                name="whatsapp"
                                size='small'
                                label="WhatsApp Number"
                                placeholder="Enter WhatsApp number"
                                value={formData.whatsapp}
                                onChange={handleInputChange}
                                error={Boolean(fieldErrors.whatsapp)}
                                helperText={fieldErrors.whatsapp}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleCloseModal}
                            sx={{
                                borderRadius: '6px',
                                textTransform: 'none',
                                color: '#222',
                                fontSize: '0.85rem',
                                px: 2.5
                            }}
                        >
                            Cancel
                        </Button>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleSubmit}
                            disabled={submitting}
                            variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 3,
                                color: '#fff',
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Add')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </PermissionWrapper>
    );
};

export default ContactUs;